import { comparer, reaction, runInAction } from 'mobx';
import type { SettingsByScope, SettingsScope } from './settings/schemas';
import type { UserConfigStore } from './UserConfigStore';

/**
 * Verbindet Felder eines MobX-Stores mit einem Bereich der UserConfig.
 *
 * Bewusst minimal-invasiv: Die Felder bleiben, wo sie sind, und alle Setter im
 * Store aendern sich nicht. Der Helper setzt beim Start die gespeicherten Werte
 * ein (`hydrate`) und beobachtet danach genau diese Felder; jede Aenderung geht
 * entprellt an `updateSettings`.
 *
 * Die Hydration laeuft ausserhalb der Reaction, damit sie nicht sofort wieder
 * ein Speichern ausloest — `updateSettings` verwirft zwar unveraenderte Patches,
 * aber so bleibt der erste Durchlauf auch ohne diesen Schutz still.
 *
 * @returns Disposer; der besitzende Store ruft ihn in seinem `dispose()` auf.
 */
export function persistSettings<S extends SettingsScope, T extends Partial<SettingsByScope[S]>>(
  target: T,
  userConfig: UserConfigStore,
  scope: S,
  keys: readonly (keyof SettingsByScope[S] & keyof T & string)[],
): () => void {
  const stored = userConfig.getSettings(scope);

  runInAction(() => {
    for (const key of keys) {
      const value = stored[key];
      if (value !== undefined) {
        (target as Record<string, unknown>)[key] = value;
      }
    }
  });

  return reaction(
    () => pick(target, keys),
    (patch) => userConfig.updateSettings(scope, patch as Partial<SettingsByScope[S]>),
    // Strukturvergleich, damit ein neues Array mit gleichem Inhalt (z. B.
    // `highlightMode`) keinen Request ausloest.
    { equals: comparer.structural },
  );
}

function pick<T extends object, K extends keyof T & string>(target: T, keys: readonly K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    result[key] = target[key];
  }
  return result;
}
